"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Stethoscope, Heart, Brain, Target, Shield, ArrowRight, BookOpen, Workflow } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const PRINCIPLES = [
  { num: "01", icon: Stethoscope, title: "Diagnose Before Treating", body: "The first rule of medicine, the most violated rule of leadership. Most organizations act on the symptom. The method demands forensic diagnosis before any intervention — pattern identification, anomaly detection, root-cause analysis. The symptom is never the disease.", gradient: "from-red/15 to-red/0", accent: "text-red" },
  { num: "02", icon: Target, title: "Triage Under Pressure", body: "When everything is urgent, nothing is solved well. The leaders who fail rarely lack the ability to act. They lack the ability to rank. Triage is not a skill of speed; it is a skill of sequencing under fear.", gradient: "from-coral/15 to-coral/0", accent: "text-coral" },
  { num: "03", icon: Workflow, title: "Run the Protocol; Keep the Judgment", body: "Protocols carry through the ninety-five percent of cases that follow the pattern. Judgment handles the five percent that don't. Brilliance without a system is unsustainable. A system without judgment cannot adapt.", gradient: "from-purple/15 to-purple/0", accent: "text-purple" },
  { num: "04", icon: Brain, title: "Own the Outcome, Not the Effort", body: "Medicine does not credit a physician for how hard the attempt was. The patient lived or did not. Organizations have lost this accountability. Effort is credited, intention is praised, hours are rewarded — and outcomes are excused.", gradient: "from-emerald/15 to-emerald/0", accent: "text-emerald" },
  { num: "05", icon: Shield, title: "First, Do No Harm", body: "The oldest instruction in medicine, the most forgotten in leadership. Under pressure, most leaders make the situation worse. They add meetings, add process, add noise, add fear. The discipline of not making it worse is the most underrated operational skill.", gradient: "from-amber-gold/15 to-amber-gold/0", accent: "text-amber-gold" },
];

export function DoctrineSection() {
  return (
    <Section id="doctrine" ariaLabel="The Doctrine" background="default">
      <SectionHeader
        icon={BookOpen}
        overline="The Doctrine"
        overlineClassName="text-emerald"
        pillClassName="glass-tinted-emerald border-emerald/20"
        title={
          <>
            The Clinical Method <span className="text-gradient-success">Applied to Organizations</span>
          </>
        }
        subtitle="Forged in clinical practice and proven across a decade of sovereign-scale operations. The operating method that commands outcomes across eighteen countries and sixty-plus integrated services."
      />

      <div className="space-y-4 max-w-4xl mx-auto">
        {PRINCIPLES.map((p, i) => (
          <motion.div
            key={p.num}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className={`group relative glass-card rounded-xl p-6 bg-gradient-to-r ${p.gradient} border border-current/10 hover:scale-[1.01] transition-all`}
          >
            <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center">
              <div className="flex items-center gap-3">
                <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                  <p.icon className="size-6 text-white" />
                </div>
                <div className={`text-display-sm leading-none font-bold ${p.accent} opacity-50`}>{p.num}</div>
              </div>
              <div>
                <h3 className="text-heading-md mb-2">{p.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
              <div className={`hidden md:flex size-14 items-center justify-center rounded-full border-2 border-dashed border-current/30 text-caption font-bold ${p.accent} opacity-50`}>
                Doctrine
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
        {[
          { cls: "text-lime", label: "Evidence" },
          { cls: "text-indigo", label: "Outcomes" },
          { cls: "text-denim", label: "Composition" },
          { cls: "text-emerald", label: "Precision" },
          { cls: "text-amber-gold", label: "Discretion" },
        ].map((s) => (
          <span key={s.label} className="glass rounded-full px-3 py-1.5 text-caption font-semibold">
            <span className={s.cls}>·</span> <span className="text-muted-foreground ml-1">{s.label}</span>
          </span>
        ))}
      </div>
    </Section>
  );
}
